'use client';

import React, { useState } from 'react';
import { LoremIpsum } from 'lorem-ipsum';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';
import GenerateButton from '@/components/ui/GenerateButton';

interface LoremOptions {
    type: 'words' | 'sentences' | 'paragraphs';
    count: number;
    minWordsPerSentence: number;
    maxWordsPerSentence: number;
    minSentencesPerParagraph: number;
    maxSentencesPerParagraph: number;
}

function GenerateLoremIpsumPage() {
    const [options, setOptions] = useState<LoremOptions>({
        type: 'paragraphs',
        count: 3,
        minWordsPerSentence: 4,
        maxWordsPerSentence: 16,
        minSentencesPerParagraph: 2,
        maxSentencesPerParagraph: 8,
    });

    const [generatedText, setGeneratedText] = useState<string>('');

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'number' ? parseInt(value) : value,
        }));
    };

    const generateLoremIpsum = () => {
        if (options.maxWordsPerSentence < options.minWordsPerSentence || options.maxSentencesPerParagraph < options.minSentencesPerParagraph) {
            return;
        }
        if (options.count < 1 || options.count > 100) {
            return;
        }
        if (options.minWordsPerSentence < 1 || options.minWordsPerSentence > 20) {
            return;
        }
        if (options.maxWordsPerSentence < 1 || options.maxWordsPerSentence > 50) {
            return;
        }
        if (options.minSentencesPerParagraph < 1 || options.minSentencesPerParagraph > 10) {
            return;
        }
        if (options.maxSentencesPerParagraph < 1 || options.maxSentencesPerParagraph > 20) {
            return;
        }
        const lorem = new LoremIpsum({
            sentencesPerParagraph: {
                max: options.maxSentencesPerParagraph,
                min: options.minSentencesPerParagraph
            },
            wordsPerSentence: {
                max: options.maxWordsPerSentence,
                min: options.minWordsPerSentence
            }
        });

        let result: string;
        switch (options.type) {
            case 'words':
                result = lorem.generateWords(options.count);
                break;
            case 'sentences':
                result = lorem.generateSentences(options.count);
                break;
            case 'paragraphs':
                result = lorem.generateParagraphs(options.count);
                break;
            default:
                result = '';
        }

        setGeneratedText(result);
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Generate Lorem Ipsum</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="radio" 
                                className="radio radio-sm mr-2" 
                                name="type" 
                                value="words" 
                                checked={options.type === 'words'} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Words</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="radio" 
                                className="radio radio-sm mr-2" 
                                name="type" 
                                value="sentences" 
                                checked={options.type === 'sentences'} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Sentences</span>
                        </label>
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="radio" 
                                className="radio radio-sm mr-2" 
                                name="type" 
                                value="paragraphs" 
                                checked={options.type === 'paragraphs'} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Paragraphs</span>
                        </label>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Number of {options.type} (1-100)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full input-sm ${ options.count < 1 || options.count > 100 ? 'input-error' : '' }`}
                            name="count" 
                            min="1" 
                            max="100"
                            value={options.count} 
                            onChange={handleOptionChange} 
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Min words per sentence (1-20)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full input-sm ${ options.minWordsPerSentence < 1 || options.minWordsPerSentence > 20 ? 'input-error' : '' }`}
                            name="minWordsPerSentence" 
                            min="1" 
                            max="20"
                            value={options.minWordsPerSentence} 
                            onChange={handleOptionChange} 
                            disabled={options.type === 'words'}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Max words per sentence (1-50)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full input-sm ${ options.maxWordsPerSentence < 1 || options.maxWordsPerSentence > 50 ? 'input-error' : '' }`}
                            name="maxWordsPerSentence" 
                            min="1" 
                            max="50"
                            value={options.maxWordsPerSentence} 
                            onChange={handleOptionChange} 
                            disabled={options.type === 'words'}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Min sentences per paragraph (1-10)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full input-sm ${ options.minSentencesPerParagraph < 1 || options.minSentencesPerParagraph > 10 ? 'input-error' : '' }`}
                            name="minSentencesPerParagraph" 
                            min="1" 
                            max="10"
                            value={options.minSentencesPerParagraph} 
                            onChange={handleOptionChange} 
                            disabled={options.type !== 'paragraphs'}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Max sentences per paragraph (1-20)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full input-sm ${ options.maxSentencesPerParagraph < 1 || options.maxSentencesPerParagraph > 20 ? 'input-error' : '' }`}
                            name="maxSentencesPerParagraph" 
                            min="1" 
                            max="20"
                            value={options.maxSentencesPerParagraph} 
                            onChange={handleOptionChange} 
                            disabled={options.type !== 'paragraphs'}
                        />
                    </div>
                    <GenerateButton generatePasswords={generateLoremIpsum} />
                </div>

                {/* Right Column - Generated Lorem Ipsum */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated Lorem Ipsum</h2>
                    <AutoGrowingTextarea
                        value={generatedText}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateLoremIpsumPage;